// Attaches a video source (MP4 or HLS) to a <video> element.
// Shared by VideoLoader (Plyr) and MinimalPlayer.

// Optimized HLS config (Bunny Stream)
const HLS_CONFIG = {
  startLevel: 2, // Start from level 2 (e.g., 720p)
  maxBufferLength: 30, // Increased buffer length
  maxMaxBufferLength: 60,
  maxBufferSize: 60 * 1000 * 1000, // 60MB
  maxBufferHole: 0.5,
  lowLatencyMode: false, // Disable if not strictly live, can improve stability
};

function isHlsUrl(url) {
  return /\.m3u8$/i.test(url || '');
}

/**
 * @param {HTMLVideoElement} video
 * @param {string} url
 * @param {Object}   [options]
 * @param {boolean}  [options.lazy=false] Only fetch the manifest until startLoad() is called.
 * @param {Function} [options.onError]    Called with a message on fatal errors.
 * @returns {{hls: Object|null, startLoad: Function}}
 * @throws {Error} when HLS is not supported in this browser
 */
function attachSource(video, url, { lazy = false, onError = null } = {}) {
  const noop = { hls: null, startLoad() {} };

  // MP4 / other progressive sources: preload="none" on the element defers loading when lazy
  if (!isHlsUrl(url)) {
    video.src = url;
    return noop;
  }

  if (typeof Hls !== 'undefined' && Hls.isSupported()) {
    const hls = new Hls(Object.assign({}, HLS_CONFIG, {
      autoStartLoad: !lazy, // lazy: only the manifest is fetched until playback starts
    }));
    hls.loadSource(url);
    hls.attachMedia(video);

    hls.on(Hls.Events.ERROR, (event, data) => {
      if (!data.fatal) {
        console.warn('HLS non-fatal error:', data.type, data.details);
        return;
      }

      console.error('HLS fatal error:', data.type, data.details);
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          break;
        default:
          hls.destroy();
          break;
      }
      if (onError) onError('HLS error: ' + data.details);
    });

    let started = !lazy;
    return {
      hls,
      startLoad() {
        if (!started) {
          started = true;
          hls.startLoad();
        }
      },
    };
  }

  // Native HLS (Safari): preload="none" on the element defers loading when lazy
  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = url;
    video.addEventListener('error', (e) => {
      console.error('Native HLS playback error:', e);
      if (onError) onError('Native HLS playback error');
    });
    return noop;
  }

  throw new Error('HLS is not supported in this browser.');
}

module.exports = { attachSource, isHlsUrl };
